import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Tab } from '../../models/tab.model';
import { IconService } from '../../services/icon.service';
import { UIIconsEnum } from '../../enums/icons.enum';

@Component({
  selector: 'app-button-scroll-tabs',
  templateUrl: './button-scroll-tabs.component.html',
  styleUrls: ['./button-scroll-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonScrollTabsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollable') scrollable!: ElementRef;
  @ViewChild('tabList') tabsList!: ElementRef;

  displayScrollButtons: boolean = true;

  constructor(
    private iconService: IconService,
    private _cdRef: ChangeDetectorRef
  ) {}
  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.needScroll());
    const scroll = this.scrollable.nativeElement as HTMLElement;

    scroll.removeEventListener('scrollend', () => this._cdRef.detectChanges());
  }
  ngAfterViewInit(): void {
    this.needScroll();
    window.addEventListener('resize', () => this.needScroll());

    const scroll = this.scrollable.nativeElement as HTMLElement;

    scroll.addEventListener('scrollend', () => this._cdRef.detectChanges());
    this._cdRef.detectChanges();
  }

  @Input() active: number = 0;
  @Input() tabs!: Array<Tab>;

  scrollToTab: number = 0;

  canScrollPrev() {
    if (!this.scrollable) return false;
    if (this.scrollToTab == 0) return false;

    const scroll = this.scrollable.nativeElement as HTMLElement;

    return Math.round(scroll.scrollLeft) !== 0;
  }

  canScrollNext() {
    if (!this.scrollable) return false;
    if (this.scrollToTab === this.tabs.length - 1) return false;

    const scroll = this.scrollable.nativeElement as HTMLElement;

    return (
      Math.round(scroll.clientWidth) !==
      Math.round(scroll.scrollWidth - scroll.scrollLeft)
    );
  }

  scrollPrev() {
    if (!this.canScrollPrev()) return;
    const scroll = this.scrollable.nativeElement as HTMLElement;
    if (this.scrollToTab <= 1) {
      scroll.scrollTo({ behavior: 'smooth', left: 0 });
      this.scrollToTab = 0;
      return;
    }

    this.scrollToTab--;
    const tabItem = (this.tabsList.nativeElement as HTMLElement).children[
      this.scrollToTab
    ] as HTMLElement | null;
    if (!tabItem) return;

    const parentRect = tabItem.parentElement?.getBoundingClientRect();
    const childRect = tabItem.getBoundingClientRect();

    scroll.scrollTo({
      behavior: 'smooth',
      left: childRect.left - (parentRect?.left ?? 0),
    });
  }

  scrollNext() {
    if (!this.canScrollNext()) return;

    this.scrollToTab++;
    const tabItem = (this.tabsList.nativeElement as HTMLElement).children[
      this.scrollToTab
    ] as HTMLElement | null;
    if (!tabItem) return;

    const scroll = this.scrollable.nativeElement as HTMLElement;

    const parentRect = tabItem.parentElement?.getBoundingClientRect();
    const childRect = tabItem.getBoundingClientRect();

    const leftScroll = childRect.left - Math.round(parentRect?.left ?? 0);

    scroll.scrollTo({
      behavior: 'smooth',
      left: leftScroll,
    });
  }

  needScroll() {
    if (!this.scrollable) return;

    const scroll = this.scrollable.nativeElement as HTMLElement;

    this.displayScrollButtons = scroll.scrollWidth > scroll.clientWidth;
  }

  selectTab(index: number) {
    this.active = index;

    this.tabs[index].click();
  }

  getIcon(name: keyof typeof UIIconsEnum) {
    return this.iconService.getUIIconByName(name);
  }
}
